import { Link } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import api from "@/lib/api";
import { useSeasonalTheme } from "@/lib/seasonal";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import Media from "@/components/Media";
import AdminModerationButtons from "@/components/AdminModerationButtons";
import RecipeCard from "@/components/RecipeCard";
import PageMeta from "@/components/PageMeta";
import { pickIcon } from "@/lib/homeIcons";
import {
  Camera, BookOpenText, ShareNetwork, Sparkle,
  Stack, Microphone, Timer, QrCode, ArrowRight, Swap,
  LinkSimple, PencilSimple, DownloadSimple,
  ShoppingCart, ListChecks, PushPin, ShoppingBag, Export,
  Calendar, CalendarBlank, ForkKnife, PencilLine, ShareFat,
} from "@phosphor-icons/react";

// Icon registry — content from the CMS is plain text. We map the 3 entry-path
// cards, the 4 how-it-works steps, and the 4 features to their icon components by index.
export const HOME_SECTION_KEYS = ["hero", "three_ways", "how_it_works", "features", "shopping_list", "meal_planner", "directory", "news", "cta"];
const ENTRY_ICONS = [Camera, LinkSimple, PencilSimple, DownloadSimple];
const ENTRY_HREFS = ["/scan", "/scan?mode=url", "/scan?mode=manual", "/import"];
const ENTRY_TESTIDS = ["home-path-scan", "home-path-link", "home-path-type", "home-path-import"];
const STEP_ICONS = [Sparkle, BookOpenText, Camera, ShareNetwork];
const FEATURE_ICONS = [Microphone, Timer, Swap, QrCode];
const SHOPPING_ICONS = [ListChecks, ShoppingCart, PushPin, ShoppingBag, Export];
const PLANNER_ICONS = [CalendarBlank, BookOpenText, PencilLine, ForkKnife, ShareFat];

// CMS links: same-site full URLs become in-app links; other http(s) links open normally.
const SITE_HOSTS = ["recipefiler.com", "www.recipefiler.com"];
const toInternal = (href) => {
  try {
    const u = new URL(href);
    if (SITE_HOSTS.includes(u.host) || u.host === window.location.host) return u.pathname + u.search + u.hash;
  } catch { /* relative path */ }
  return href;
};
const SmartLink = ({ href, children, ...rest }) => {
  const to = toInternal(href || "/");
  return /^https?:\/\//.test(to)
    ? <a href={to} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>
    : <Link to={to} {...rest}>{children}</Link>;
};
// Box with an optional CMS link — clickable card when `href` is set, plain box otherwise.
const Tile = ({ href, className, children, ...rest }) => (href
  ? <SmartLink href={href} className={`${className} block hover:ring-2 hover:ring-rf-amber transition-shadow`} {...rest}>{children}</SmartLink>
  : <div className={className} {...rest}>{children}</div>);

// Replace {FREE} tokens in editable copy with the runtime free-recipe limit
const interp = (s, free) => (typeof s === "string" ? s.replaceAll("{FREE}", String(free)) : s);

export default function Home() {
  const { flags, limits } = useSiteFlags();
  const FREE = limits?.free_recipe_limit ?? 10;
  const [publicRecipes, setPublicRecipes] = useState([]);
  const [posts, setPosts] = useState([]);
  // settings starts as `null` (not `{}`) so we can distinguish "still loading"
  // from "loaded but empty" — otherwise the hero briefly rendered the fallback
  // Unsplash photo before the real hero_image_url arrived, causing a
  // visible image-swap flash on every page load (reported 2026-07-08).
  const [settings, setSettings] = useState(null);
  const [content, setContent] = useState(null);
  const seasonal = useSeasonalTheme();

  useEffect(() => {
    // Fetch NEWEST public recipes (not alpha) so the "From the public
    // directory" strip feels fresh on every home visit. The Directory page
    // itself keeps the A-Z default. `sort=newest` is already supported by
    // /directory/recipes and used for the SEO/social preview cards.
    api.get("/directory/recipes?limit=6&sort=newest").then((r) => setPublicRecipes(r.data)).catch(() => {});
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => setSettings({}));
    api.get("/public/site-content/home").then((r) => setContent(r.data.content)).catch(() => {});
  }, []);

  // Wait for BOTH content AND settings before rendering the hero — otherwise
  // the hero-image URL races with the CMS copy and briefly renders a stale
  // fallback. Both endpoints are fast (<150ms typical); combined wait is
  // imperceptible to users but eliminates the flash entirely.
  if (!content || !settings) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center" data-testid="home-loading">
        <div className="w-8 h-8 border-2 border-rf-orange border-t-transparent rounded-full animate-spin" aria-hidden="true" />
      </div>
    );
  }

  // Fallback Unsplash photo used ONLY when the admin has never set a hero
  // image (fresh install). Once settings resolves this never renders during
  // normal use — it's the "static default" you'd see on a brand-new instance.
  const heroImage = seasonal?.hero_url
    ? `${process.env.REACT_APP_BACKEND_URL}${seasonal.hero_url}`
    : settings.hero_image_url || "https://images.unsplash.com/photo-1556909115-3f381036bcb3?w=1200&q=85";
  const demoRecipeId = publicRecipes?.[0]?.recipe_id;
  const kitchenViewHref = demoRecipeId ? `/k/${demoRecipeId}` : "/how-to-use";

  const hero = content.hero;
  const tw = content.three_ways;
  const hiw = content.how_it_works;
  const feat = content.features;
  const shop = content.shopping_list;
  const plan = content.meal_planner;
  const dir = content.directory;
  const news = content.news;
  const cta = content.cta;
  // SEO is fully admin-editable via Admin → Site Content → Home → SEO.
  // Defaults live in `HOME_DEFAULTS.seo` (backend) so a fresh install still
  // ships useful metadata; overrides merge on top server-side.
  const seo = content.seo || {};

  // Each home section as a block so Site Copy can reorder (layout.order) and schedule them (show_from / show_until).
  const blocks = {};
  blocks.hero = (
      !hero.hidden && (
      <section className="relative bg-paper overflow-hidden">
        <div className="bg-noise" />
        {/* Desktop: whole hero (headline, photo, CTAs) sized to fit one screen via vh units; mobile unchanged. */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-24 lg:py-[4vh] lg:min-h-[calc(100svh-81px)] lg:flex lg:items-center relative" data-testid="home-hero">
          <div className="grid lg:grid-cols-12 gap-12 items-center w-full">
            <div className="lg:col-span-7 animate-fade-up">
              <p className="overline mb-6 lg:mb-[2.5vh]">{hero.overline}</p>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-[clamp(2.75rem,min(5.2vw,8.5vh),6rem)] font-black leading-[0.95] tracking-tight text-rf-ink mb-8 lg:mb-[3vh]">
                {hero.h1_line1}<br />
                <span className="text-rf-orange">{hero.h1_line2_orange}</span><br />
                {hero.h1_line3}
              </h1>
              <p className="text-lg md:text-xl lg:text-[clamp(1rem,2.4vh,1.25rem)] text-rf-ink-soft max-w-xl leading-relaxed mb-10 lg:mb-[3.5vh]">
                {hero.body}
              </p>
              <div className="flex flex-wrap gap-4 lg:gap-3">
                <SmartLink href={hero.cta_primary_href || "/register"} data-testid="hero-cta-start" className="btn-amber text-lg lg:text-base">
                  {interp(hero.cta_primary_label, FREE)}
                </SmartLink>
                <SmartLink href={hero.cta_try_href || "/try"} data-testid="hero-cta-try" className="btn-outline-amber text-lg lg:text-base">
                  {hero.cta_try_label || "Try it free · No sign-up"}
                </SmartLink>
                <SmartLink href={hero.cta_howto_href || "/how-to-use"} data-testid="hero-cta-howto" className="btn-outline-amber text-lg lg:text-base">
                  {hero.cta_howto_label || "How To Use"}
                </SmartLink>
                <SmartLink href={hero.cta_secondary_href || "/directory"} data-testid="hero-cta-directory" className="btn-outline-ink text-lg lg:text-base">
                  {hero.cta_secondary_label}
                </SmartLink>
              </div>
              <div className="mt-10 lg:mt-[3vh] flex items-center gap-6 text-sm text-rf-ink-soft">
                <span>{hero.trust_1}</span>
                <span>{hero.trust_2}</span>
                <span>{hero.trust_3}</span>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] lg:aspect-auto lg:h-[min(calc(100svh-200px),680px)] rounded-3xl overflow-hidden shadow-2xl rotate-2">
                <Media
                  src={heroImage}
                  alt={seasonal ? `${seasonal.name} illustration` : "A multi-generational family cooking together from a handwritten family recipe card in a warm home kitchen"}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-rf-ink/40 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-rf-amber rounded-2xl p-6 shadow-xl rotate-[-3deg] max-w-[240px]">
                <p className="font-display font-black text-rf-ink text-2xl leading-tight">{hero.badge_text}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )
  );
  blocks.three_ways = (
      !tw.hidden && (
      <section className="py-20 lg:py-24 bg-paper" data-testid="home-three-ways-in">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-12">
            <p className="overline mb-4">{tw.overline}</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black leading-tight tracking-tight text-rf-ink">
              {tw.h2_line1}<br />{tw.h2_line2}<br /><span className="text-rf-orange">{tw.h2_line3_orange}</span>
            </h2>
            <p className="text-rf-ink-soft text-lg mt-6 max-w-2xl leading-relaxed">
              {tw.intro}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tw.paths.map((p, i) => {
              const Icon = pickIcon(p.icon, ENTRY_ICONS[i] || Camera);
              return (
                <SmartLink
                  key={i}
                  href={p.href || ENTRY_HREFS[i] || "/scan"}
                  data-testid={ENTRY_TESTIDS[i] || `home-path-${i}`}
                  className="card-recipe hover-lift group block relative overflow-hidden"
                >
                  <div className="w-14 h-14 rounded-xl bg-rf-amber/20 flex items-center justify-center mb-5 group-hover:bg-rf-amber/40 transition-colors">
                    <Icon size={32} weight="duotone" className="text-rf-orange" />
                  </div>
                  <p className="text-[11px] font-black tracking-[0.18em] uppercase text-rf-orange mb-2">{p.eyebrow}</p>
                  <h3 className="font-display font-extrabold text-2xl text-rf-ink mb-3 leading-tight">{p.title}</h3>
                  <p className="text-rf-ink-soft leading-relaxed mb-5">{p.body}</p>
                  <span className="inline-flex items-center gap-1 text-rf-orange font-bold text-sm group-hover:gap-2 transition-all">
                    {p.cta} <ArrowRight size={16} weight="bold" />
                  </span>
                </SmartLink>
              );
            })}
          </div>
          <p className="text-center text-rf-ink-soft text-sm mt-10 italic max-w-2xl mx-auto">
            {tw.footer_text}
          </p>
          <p className="text-center text-sm mt-3" data-testid="home-ios-share-cta">
            <span className="text-rf-ink-soft">{tw.ios_label} </span>
            <SmartLink href={tw.ios_link_href || "/ios-share-setup"} className="text-rf-orange font-bold hover:underline">
              {tw.ios_link_text}
            </SmartLink>
            <span className="text-rf-ink-soft"> {tw.ios_suffix}</span>
          </p>
        </div>
      </section>
      )
  );
  blocks.how_it_works = (
      !hiw.hidden && (
      <section className="py-24 bg-rf-cream-alt" data-testid="home-how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-14">
            <p className="overline mb-4">{hiw.overline}</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black leading-tight tracking-tight text-rf-ink">
              {hiw.h2_line1}<br />{hiw.h2_line2}<br />{hiw.h2_line3}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {hiw.steps.map((s, i) => {
              const Icon = pickIcon(s.icon, STEP_ICONS[i] || Sparkle);
              return (
                <Tile key={i} href={s.href} data-testid={`home-step-${i}`} className="card-recipe hover-lift">
                  <div className="w-14 h-14 rounded-xl bg-rf-amber/20 flex items-center justify-center mb-5">
                    <Icon size={32} weight="duotone" className="text-rf-orange" />
                  </div>
                  <p className="text-xs font-bold text-rf-orange mb-2">STEP {i + 1}</p>
                  <h3 className="font-display font-extrabold text-xl text-rf-ink mb-3">{s.title}</h3>
                  <p className="text-rf-ink-soft leading-relaxed">{s.body}</p>
                </Tile>
              );
            })}
          </div>
        </div>
      </section>
      )
  );
  blocks.features = (
      !feat.hidden && (
      <section className="py-24 bg-paper" data-testid="home-features">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="rounded-3xl bg-rf-ink text-rf-cream p-10 lg:p-14 relative overflow-hidden">
            <div className="grid lg:grid-cols-12 gap-10 relative z-10">
              <div className="lg:col-span-6">
                <p className="overline text-rf-amber mb-4">{feat.overline}</p>
                <h3 className="font-display text-3xl lg:text-5xl font-black mb-5 leading-tight">
                  {feat.h3_line1}<br />{feat.h3_line2}
                </h3>
                <p className="text-rf-cream/70 text-lg mb-8 max-w-md leading-relaxed">
                  {feat.body}
                </p>
                <SmartLink href={feat.cta_href || kitchenViewHref} data-testid="see-it-in-action" className="btn-amber">{feat.cta_label}</SmartLink>
              </div>
              <div className="lg:col-span-6 grid sm:grid-cols-2 gap-5 content-start">
                {feat.items.slice(0, 3).map((f, i) => {
                  const Icon = pickIcon(f.icon, FEATURE_ICONS[i] || Microphone);
                  return (
                    <Tile key={i} href={f.href} data-testid={`home-feature-tile-${i}`} className="rounded-2xl bg-rf-cream/5 border border-rf-cream/10 p-5">
                      <Icon size={28} weight="duotone" className="text-rf-amber mb-3" />
                      <h4 className="font-display font-extrabold text-lg mb-1.5">{f.title}</h4>
                      <p className="text-rf-cream/70 text-sm leading-relaxed">{f.body}</p>
                    </Tile>
                  );
                })}
              </div>
            </div>
            <div className="absolute right-0 bottom-0 w-72 h-72 bg-rf-amber/10 rounded-full -mb-32 -mr-32" />
          </div>
        </div>
      </section>
      )
  );
  blocks.shopping_list = (
      shop && !shop.hidden && (
        <section className="py-20 lg:py-24 bg-rf-amber/10" data-testid="home-shopping-section">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-5">
                <p className="overline mb-4">{shop.overline}</p>
                <h3 className="font-display text-4xl md:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight text-rf-ink mb-6">
                  {shop.h3_line1}<br />
                  <span className="text-rf-orange">{shop.h3_line2}</span>
                </h3>
                <p className="text-rf-ink-soft text-lg leading-relaxed mb-8 max-w-md">
                  {shop.body}
                </p>
                <SmartLink href={shop.cta_href || "/shopping"} data-testid="home-shopping-cta" className="btn-orange text-base inline-flex items-center gap-2">
                  {shop.cta_label} <ArrowRight size={18} weight="bold" />
                </SmartLink>
              </div>
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                {(shop.items || []).map((it, i) => {
                  const Icon = pickIcon(it.icon, SHOPPING_ICONS[i] || ShoppingCart);
                  return (
                    <Tile key={i} href={it.href} className="card-recipe hover-lift" data-testid={`home-shopping-tile-${i}`}>
                      <div className="w-12 h-12 rounded-xl bg-rf-amber/20 flex items-center justify-center mb-4">
                        <Icon size={26} weight="duotone" className="text-rf-orange" />
                      </div>
                      <h4 className="font-display font-extrabold text-lg text-rf-ink mb-1.5">{it.title}</h4>
                      <p className="text-rf-ink-soft text-sm leading-relaxed">{it.body}</p>
                    </Tile>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )
  );
  blocks.meal_planner = (
      plan && !plan.hidden && (
        <section className="py-20 lg:py-24 bg-paper" data-testid="home-meal-planner-section">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            {/* Image-column swaps sides vs the shopping section so the two
                feel like a pair rather than repeats of the same layout. */}
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4 order-2 lg:order-1">
                {(plan.items || []).map((it, i) => {
                  const Icon = pickIcon(it.icon, PLANNER_ICONS[i] || Calendar);
                  return (
                    <Tile key={i} href={it.href} className="card-recipe hover-lift" data-testid={`home-planner-tile-${i}`}>
                      <div className="w-12 h-12 rounded-xl bg-rf-amber/20 flex items-center justify-center mb-4">
                        <Icon size={26} weight="duotone" className="text-rf-orange" />
                      </div>
                      <h4 className="font-display font-extrabold text-lg text-rf-ink mb-1.5">{it.title}</h4>
                      <p className="text-rf-ink-soft text-sm leading-relaxed">{it.body}</p>
                    </Tile>
                  );
                })}
              </div>
              <div className="lg:col-span-5 order-1 lg:order-2">
                <p className="overline mb-4">{plan.overline}</p>
                <h3 className="font-display text-4xl md:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight text-rf-ink mb-6">
                  {plan.h3_line1}<br />
                  <span className="text-rf-orange">{plan.h3_line2}</span>
                </h3>
                <p className="text-rf-ink-soft text-lg leading-relaxed mb-8 max-w-md">
                  {plan.body}
                </p>
                <SmartLink href={plan.cta_href || "/planner"} data-testid="home-planner-cta" className="btn-orange text-base inline-flex items-center gap-2">
                  {plan.cta_label} <ArrowRight size={18} weight="bold" />
                </SmartLink>
              </div>
            </div>
          </div>
        </section>
      )
  );
  blocks.directory = (
      !dir.hidden && (
      <section className="py-14 bg-rf-cream-alt" data-testid="home-directory">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between mb-8 gap-4">
            <div>
              <p className="overline mb-3">{dir.overline}</p>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight text-rf-ink">{dir.h2}</h2>
            </div>
            <SmartLink href={dir.link_href || "/directory"} data-testid="home-directory-link" className="btn-outline-ink">
              {dir.link_label} <ArrowRight size={18} />
            </SmartLink>
          </div>
          {publicRecipes.length === 0 ? (
            <div className="card-recipe text-center py-16">
              <Stack size={48} className="mx-auto text-rf-orange mb-4" />
              <p className="text-rf-ink-soft">{dir.empty_message}</p>
              <SmartLink href={dir.empty_cta_href || "/register"} data-testid="home-directory-empty-cta" className="btn-amber mt-6 inline-flex">{dir.empty_cta}</SmartLink>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {publicRecipes.map((r) => (
                <RecipeCard
                  key={r.recipe_id}
                  recipe={r}
                  onAdminDelete={(rid) => setPublicRecipes((prev) => prev.filter((x) => x.recipe_id !== rid))}
                />
              ))}
            </div>
          )}
        </div>
      </section>
      )
  );
  blocks.news = (
      posts.length > 0 && !news.hidden && (
        <section className="py-24 bg-paper">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex flex-wrap items-end justify-between mb-12 gap-4">
              <div>
                <p className="overline mb-3">{news.overline}</p>
                <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight text-rf-ink">{news.h2}</h2>
              </div>
              <SmartLink href={news.link_href || "/news"} data-testid="home-news-link" className="btn-outline-ink">{news.link_label || "All articles"} <ArrowRight size={18} /></SmartLink>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {posts.map((p) => (
                <Link key={p.post_id} to={`/news/${p.slug}`} className="card-recipe hover-lift block overflow-hidden">
                  {p.images?.[0] && (
                    <div className="-m-6 mb-4 aspect-[16/10] overflow-hidden">
                      <Media src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <h3 className="font-display font-extrabold text-xl text-rf-ink mb-2 line-clamp-2">{p.title}</h3>
                  <p className="text-rf-ink-soft text-sm line-clamp-3">{p.seo_summary}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )
  );
  blocks.cta = (
      !cta.hidden && (
      <section className="py-24 bg-rf-amber" data-testid="home-final-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-4xl md:text-6xl font-black text-rf-ink mb-6 leading-tight">
            {cta.h2_line1}<br />{cta.h2_line2}
          </h2>
          <p className="text-rf-ink text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            {flags.subscriptions_enabled ? interp(cta.body_paid, FREE) : interp(cta.body_free, FREE)}
          </p>
          <SmartLink href={cta.button_href || "/register"} data-testid="home-final-cta" className="btn-ink text-lg">{cta.button_label}</SmartLink>
        </div>
      </section>
      )
  );
  const today = new Date().toLocaleDateString("en-CA"); // local YYYY-MM-DD
  const isScheduledLive = (k) => {
    const c = content[k] || {};
    return !(c.show_from && today < c.show_from) && !(c.show_until && today > c.show_until);
  };
  const order = [...(content.layout?.order || []), ...HOME_SECTION_KEYS]
    .filter((k, i, a) => HOME_SECTION_KEYS.includes(k) && a.indexOf(k) === i);

  return (
    <div>
      <PageMeta
        title={seo.seo_title || "Recipe Filer — Save family recipes, scan & print cookbooks"}
        description={seo.seo_description || "Recipe Filer keeps family recipes alive — scan Nan's handwritten cards, tidy them with AI, share with loved ones and print your own hardback cookbook."}
        keywords={seo.seo_keywords || "Recipe Filer, recipe filer, family recipe book, family cookbook app, scan handwritten recipes, digitise handwritten recipe cards, digital cookbook, recipe organiser UK, print your own cookbook, print A5 hardback cookbook UK, hardback recipe book print on demand, share family recipes, AI recipe scanner, OCR handwritten recipe scanner, AI recipe transcription, save grandma's recipes, save Nan's handwritten recipes, family heirloom cookbook maker, personalised family cookbook UK, cookbook maker, meal planner with shopping list, collaborative family recipe app, convert recipe cards to digital, home cook recipe manager"}
        includeWebsiteSchema
      />
      {order.filter(isScheduledLive).map((k) => <Fragment key={k}>{blocks[k] || null}</Fragment>)}
    </div>
  );
}
